import { theme } from '@kijiji/theme'

import {
  type AdSizesMap,
  type GptGlobalProperties,
  type SizeMappings,
  type SizeMappingsGPT,
} from '@/features/advertisement/types/adConfig'

export const GPT_COMPONENT_ID: Record<string, string> = {
  TOP_LEADERBOARD: 'gpt-leaderboard-top',
  SKYSCRAPER: 'gpt-skyscraper',
  SKYSCRAPER_UPPER_RIGHT: 'gpt-skyscraper_upperright',
  SKYSCRAPER_LOWER_LEFT: 'gpt-skyscraper_lowerleft',
  INLINE: 'gpt-inline-srp',
  INLINE_1: 'gpt-inline-srp_1',
  INLINE_2: 'gpt-inline-srp_2',
  INLINE_3: 'gpt-inline-srp_3',
  INLINE_4: 'gpt-inline-srp_4',
  INLINE_MAP_SRP: 'gpt-inline-map-srp',
  MINI_LEADERBOARD: 'gpt-minileaderboard-base',
  BASE_LEADERBOARD: 'gpt-leaderboard-base',
  MWEB_LEADERBOARD: 'gpt-sticky',
  TAKEOVER: 'gpt-takeover',
}

export const SIZE_MAPPING_NAMES: Record<string, string> = {
  [GPT_COMPONENT_ID.TOP_LEADERBOARD]: 'TOP_LEADERBOARD',
  [GPT_COMPONENT_ID.SKYSCRAPER]: 'SKYSCRAPER',
  [GPT_COMPONENT_ID.SKYSCRAPER_UPPER_RIGHT]: 'SKYSCRAPER_UPPER_RIGHT',
  [GPT_COMPONENT_ID.SKYSCRAPER_LOWER_LEFT]: 'SKYSCRAPER_LOWER_LEFT',
  [GPT_COMPONENT_ID.INLINE]: 'INLINE',
  [GPT_COMPONENT_ID.INLINE_MAP_SRP]: 'INLINE_MAP_SRP',
  [GPT_COMPONENT_ID.MINI_LEADERBOARD]: 'MINI_LEADERBOARD',
  [GPT_COMPONENT_ID.BASE_LEADERBOARD]: 'BASE_LEADERBOARD',
  [GPT_COMPONENT_ID.MWEB_LEADERBOARD]: 'MWEB_LEADERBOARD',
  [GPT_COMPONENT_ID.TAKEOVER]: 'TAKEOVER',
}

/**
 * global ad path; can be overriden by specifying in ad slots below
 */
export const AD_PATH_PROD_FR = '/5138/kij.ca.buyandsell-fr'
export const AD_PATH_PROD_EN = '/5138/kij.ca.buyandsell'
export const AD_PATH_TEST = '/5138/test_got'

export const AD_NETWORK_CODE = '5138'
export const AD_PATH_BASE = 'kij.ca.'
export const AD_PATH_L0 = 'search' // L0 is All Categories

export const LEADERBOARD_SCROLL_STICKY_OFFSET = '-150px'
export const INLINE_AD_BREAKPOINT = 1125

// 30 seconds, as recommended by Google
export const AD_REFRESH_COOLDOWN_EXTENDED = 30000
export const AD_REFRESH_COOLDOWN_LEGACY = 8000

/**
 * Used for CSS media queries
 * Ad sizes based on:
 * https://miro.com/app/board/uXjVOhxPqW0=/?moveToWidget=3458764536375267082&cot=14
 * https://www.figma.com/file/KtI3PVDoTvo3RoSvZ7NnHJ/Search-Results-Page?node-id=7336%3A317069
 */
export const AD_SLOTS = {
  LEADERBOARD: {
    WIDTH: '728px',
    HEIGHT: '90px',
  },
  LEADERBOARD_L: {
    WIDTH: '970px',
    HEIGHT: '90px',
  },
  LEADERBOARD_XL: {
    WIDTH: '970px',
    HEIGHT: '250px',
  },
  INLINE_S: {
    WIDTH: '300px',
    HEIGHT: '250px',
  },
  INLINE_M: {
    WIDTH: '728px',
    HEIGHT: '250px',
  },
  MINI_LEADERBOARD_S: {
    WIDTH: '328px',
    HEIGHT: '136px',
  },
  MINI_LEADERBOARD_M: {
    WIDTH: '664px',
    HEIGHT: '208px',
  },
  MINI_LEADERBOARD_L: {
    WIDTH: '956px',
    HEIGHT: '208px',
  },
  BASE_LEADERBOARD_S: {
    WIDTH: '320px',
    HEIGHT: '250px',
  },
  MOBILE_LEADERBOARD: {
    WIDTH: '320px',
    HEIGHT: '50px',
  },
}

// based on values that were coming back from GOT
export const adSizesMap: AdSizesMap = {
  [GPT_COMPONENT_ID.TOP_LEADERBOARD]: [
    [728, 90],
    [970, 90],
  ], // not on mobile
  [GPT_COMPONENT_ID.SKYSCRAPER]: [
    [160, 600],
    [300, 250],
    [300, 600],
  ], // only on desktop
  [GPT_COMPONENT_ID.SKYSCRAPER_UPPER_RIGHT]: [
    [160, 600],
    [300, 250],
    [300, 600],
  ],
  [GPT_COMPONENT_ID.SKYSCRAPER_LOWER_LEFT]: [
    [160, 600],
    [300, 250],
    [300, 600],
  ],
  [GPT_COMPONENT_ID.INLINE]: [
    [300, 250],
    [320, 50], // mweb only size
    [728, 90],
    [970, 90],
    [970, 250],
  ],
  [GPT_COMPONENT_ID.INLINE_MAP_SRP]: [
    [300, 250],
    [320, 50], // mweb only size
  ],
  [GPT_COMPONENT_ID.MINI_LEADERBOARD]: [
    [328, 136], // mweb only size
    [575, 210],
    [956, 208],
  ],
  [GPT_COMPONENT_ID.BASE_LEADERBOARD]: [
    [300, 250],
    [320, 50],
    [728, 90],
    [970, 90],
    [970, 250],
  ],
  [GPT_COMPONENT_ID.MWEB_LEADERBOARD]: [
    [300, 50],
    [320, 50],
  ],
  [GPT_COMPONENT_ID.TAKEOVER]: [
    [1920, 1080],
    [1728, 1080],
  ],
}

// these sizeMappings are used in different formats by GPT and Prebid
export const SIZE_MAPPINGS: SizeMappings = {
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.TOP_LEADERBOARD]]: {
    TABLET: [adSizesMap[GPT_COMPONENT_ID.TOP_LEADERBOARD][0]], // only 728x90
    DESKTOP: adSizesMap[GPT_COMPONENT_ID.TOP_LEADERBOARD],
  },
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.SKYSCRAPER]]: {
    TABLET: [], // not on tablet
    DESKTOP: adSizesMap[GPT_COMPONENT_ID.SKYSCRAPER],
  },
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.INLINE]]: {
    MOBILE: [
      adSizesMap[GPT_COMPONENT_ID.INLINE][0], // 300x250
      adSizesMap[GPT_COMPONENT_ID.INLINE][1], // 320x50
    ],
    TABLET: [
      adSizesMap[GPT_COMPONENT_ID.INLINE][0], // 300x250
      adSizesMap[GPT_COMPONENT_ID.INLINE][2], // 728x90
    ],
    DESKTOP_LARGE: adSizesMap[GPT_COMPONENT_ID.INLINE].filter((_, index) => index !== 1), // remove 320x50
  },
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.INLINE_MAP_SRP]]: {
    MOBILE: [
      adSizesMap[GPT_COMPONENT_ID.INLINE][0], // 300x250
      adSizesMap[GPT_COMPONENT_ID.INLINE][1], // 320x50
    ],
  },
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.MINI_LEADERBOARD]]: {
    MOBILE: [adSizesMap[GPT_COMPONENT_ID.MINI_LEADERBOARD][0]], // 328x136
    TABLET: [
      adSizesMap[GPT_COMPONENT_ID.MINI_LEADERBOARD][1], // 575x210
    ],
    DESKTOP: [
      adSizesMap[GPT_COMPONENT_ID.MINI_LEADERBOARD][1], // 575x210
      adSizesMap[GPT_COMPONENT_ID.MINI_LEADERBOARD][2], // 956x208
    ],
  },
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.BASE_LEADERBOARD]]: {
    MOBILE: [
      adSizesMap[GPT_COMPONENT_ID.BASE_LEADERBOARD][0], // 300x250
      adSizesMap[GPT_COMPONENT_ID.BASE_LEADERBOARD][1], // 320x50
    ],
    TABLET: [
      adSizesMap[GPT_COMPONENT_ID.BASE_LEADERBOARD][2], // 728x90
    ],
    DESKTOP: [
      adSizesMap[GPT_COMPONENT_ID.BASE_LEADERBOARD][2], // 728x90
      adSizesMap[GPT_COMPONENT_ID.BASE_LEADERBOARD][3], // 970x90
      adSizesMap[GPT_COMPONENT_ID.BASE_LEADERBOARD][4], // 970x250
    ],
  },
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.MWEB_LEADERBOARD]]: {
    MOBILE: adSizesMap[GPT_COMPONENT_ID.MWEB_LEADERBOARD],
  },
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.TAKEOVER]]: {
    MOBILE: [], // No takeover on mobile
    TABLET: [], // No takeover on tablet
    DESKTOP: adSizesMap[GPT_COMPONENT_ID.TAKEOVER], // Only on desktop
  },
}

const SIZE_MAPPINGS_GPT: SizeMappingsGPT = {
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.TOP_LEADERBOARD]]: [
    {
      viewPortSize: [0, 0],
      sizes: [],
    },
    {
      viewPortSize: [theme.breakpoints.medium, 0],
      sizes: SIZE_MAPPINGS.TOP_LEADERBOARD.TABLET, // only 728x90
    },
    {
      viewPortSize: [theme.breakpoints.large, 0],
      sizes: SIZE_MAPPINGS.TOP_LEADERBOARD.DESKTOP,
    },
  ],
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.SKYSCRAPER]]: [
    {
      viewPortSize: [0, 0],
      sizes: [],
    },
    {
      viewPortSize: [theme.breakpoints.medium, 0],
      sizes: SIZE_MAPPINGS.SKYSCRAPER.TABLET,
    },
    {
      viewPortSize: [theme.breakpoints.large, 0], // only on desktop
      sizes: SIZE_MAPPINGS.SKYSCRAPER.DESKTOP,
    },
  ],
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.INLINE]]: [
    {
      viewPortSize: [0, 0],
      sizes: SIZE_MAPPINGS.INLINE.MOBILE,
    },
    {
      viewPortSize: [theme.breakpoints.medium, 0],
      sizes: SIZE_MAPPINGS.INLINE.TABLET,
    },
    {
      viewPortSize: [theme.breakpoints.xLarge, 0],
      sizes: SIZE_MAPPINGS.INLINE.DESKTOP_LARGE,
    },
  ],
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.INLINE_MAP_SRP]]: [
    {
      viewPortSize: [0, 0],
      sizes: SIZE_MAPPINGS.INLINE_MAP_SRP.MOBILE,
    },
  ],
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.MINI_LEADERBOARD]]: [
    {
      viewPortSize: [0, 0],
      sizes: SIZE_MAPPINGS.MINI_LEADERBOARD.MOBILE,
    },
    {
      viewPortSize: [theme.breakpoints.medium, 0],
      sizes: SIZE_MAPPINGS.MINI_LEADERBOARD.TABLET,
    },
    {
      viewPortSize: [theme.breakpoints.large, 0],
      sizes: SIZE_MAPPINGS.MINI_LEADERBOARD.DESKTOP,
    },
  ],
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.BASE_LEADERBOARD]]: [
    {
      viewPortSize: [0, 0],
      sizes: SIZE_MAPPINGS.BASE_LEADERBOARD.MOBILE,
    },
    {
      viewPortSize: [theme.breakpoints.medium, 0],
      sizes: SIZE_MAPPINGS.BASE_LEADERBOARD.TABLET,
    },
    {
      viewPortSize: [theme.breakpoints.large, 0],
      sizes: SIZE_MAPPINGS.BASE_LEADERBOARD.DESKTOP,
    },
  ],
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.MWEB_LEADERBOARD]]: [
    {
      viewPortSize: [0, 0],
      sizes: SIZE_MAPPINGS.MWEB_LEADERBOARD.MOBILE,
    },
    {
      viewPortSize: [theme.breakpoints.medium, 0],
      sizes: [], // not on tablet
    },
  ],
  [SIZE_MAPPING_NAMES[GPT_COMPONENT_ID.TAKEOVER]]: [
    {
      viewPortSize: [0, 0],
      sizes: [], // not on mobile
    },
    {
      viewPortSize: [theme.breakpoints.medium, 0],
      sizes: [], // not on tablet
    },
    {
      viewPortSize: [theme.breakpoints.large, 0],
      sizes: SIZE_MAPPINGS.TAKEOVER.DESKTOP, // only on desktop
    },
  ],
}

/**
 * global ad config to be passed to the AdvertisingProvider
 * we are now using hardcoded ad sizes to override the ones coming from GOT
 */
export const GPT_GLOBAL_PROPERTIES = (): GptGlobalProperties => ({
  path: AD_PATH_PROD_EN,
  enableLazyLoad: {
    marginPercent: 150,
    mobileScaling: 2,
  },
  usePrebid: false,
  useAPS: false,
  sizeMappings: SIZE_MAPPINGS_GPT,
})

export const INLINE_AD_POSITIONS = [7, 15, 23, 31]
/**
 * Same positions but for grid view
 * It will ensure the ads are properly placed when there are 3 items in a row
 */
export const INLINE_GRID_AD_POSITIONS = [8, 17, 26, 35]
